var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { PanelNames } from '../../UtilsClasses/FactoryConstants';
import { ApplicationPanelWithTable } from '../ApplicationPanelWithTable';
import OptionPaperPositionsPanelTemplate from '@front/templates/OptionPaperPositionsPanelTemplate.html';
import { PaperPositionItem } from '../../cache/PaperPositionItem';
import { Resources } from '@shared/localizations/Resources';
import { SessionSettings } from '@shared/commons/SessionSettings';
import { Quantity } from '@shared/commons/UtilsClasses/Quantity';
import { OrderType } from '@shared/utils/Trading/OrderType';
import { SlTpUtils } from '@shared/utils/Trading/SlTpUtils';
import { SlTpPriceType } from '@shared/utils/Enums/Constants';
import { LinkedPriceType } from '@shared/commons/cache/SavedOrders/SavedOrderEnums';
import { OperationType } from '@shared/utils/Trading/OperationType';
import { SavedOrderValidator } from '@shared/commons/cache/SavedOrders/SavedOrderValidator';
import { ThemeManager } from '../../misc/ThemeManager';
import { SavedOrdersController } from '@shared/commons/cache/SavedOrders/SavedOrdersController';
import { MainWindowManager } from '../../UtilsClasses/MainWindowManager';
import { IsAllowed } from '@shared/commons/IsAllowed';
import { PlacedFrom } from '@shared/utils/Trading/PlacedFrom';
import { Instrument } from '@shared/commons/cache/Instrument';
import { TradingPositionType } from '@shared/utils/Instruments/TradingPositionType';
import { Color } from '@shared/commons/Graphics';
import { WDSettings } from '../../settings/WDGeneralSettings';
import { DataCache } from '@shared/commons/DataCache';
import { BaseSettings } from '@shared/commons/Settings/BaseGeneralSettingsWrapper';
import { OrderTypeColumnRenderer } from '../../elements/QuickTable/Renderers/OrderTypeColumnRenderer';
import { createTagDictionary, NewContextMenuItemType } from '@shared/utils/ContextMenuUtils';
export class OptionPaperPositionsPanel extends ApplicationPanelWithTable {
    constructor() {
        super();
        this._isInitialized = false;
        this.onOpenOrderEntryPanel = (panel) => {
            panel.OnClose.Subscribe(this.onCloseOrderEntryPanel, this);
            panel.set({
                placeButtonAdditionalClass: 'alertStyle',
                placeButtonLocalizationKey: 'panel.newOrderEntry.AddPaperPosition',
                accountVisible: false,
                isEnabledInstrumentLookup: false,
                isEnabledAccountLookup: false,
                isCreateOrderParmeters: true,
                showCustomFullscreenCloud: true,
                account: this._optionTrader.account,
                instrument: this._optionTrader.underlier,
                forbiddenOrderTypes: [OrderType.OCO]
            });
            panel.NewParametrsHandler = this.onCreateSavedOrder;
        };
        this.onCreateSavedOrder = (orderEdit) => {
            this._optionTrader.createPaperPositionFromOrderEdit(orderEdit);
            orderEdit.dispose();
        };
        this.onCloseOrderEntryPanel = (panel) => {
            panel.OnClose.UnSubscribe(this.onCloseOrderEntryPanel, this);
            panel.NewParametrsHandler = null;
        };
        this.Name = 'OptionPaperPositionsPanel';
        this.headerLocaleKey = 'panel.optionPaperPositions';
    }
    getType() {
        return PanelNames.OptionPaperPositionsPanel;
    }
    oninit() {
        super.oninit();
        this.clearTable = this.clearTable.bind(this);
        this.onAfterEditItem = this.onAfterEditItem.bind(this);
        this.onAddPaperPosition = this.onAddPaperPosition.bind(this);
        this.onRemovePaperPosition = this.onRemovePaperPosition.bind(this);
        this.onUpdatePaperPosition = this.onUpdatePaperPosition.bind(this);
        this.processEditRow = this.processEditRow.bind(this);
        this.onAddUnderlierBtnClicked = this.onAddUnderlierBtnClicked.bind(this);
        this.onCancelAllBtnClicked = this.onCancelAllBtnClicked.bind(this);
        this.onPlaceSelectedBtnClicked = this.onPlaceSelectedBtnClicked.bind(this);
        super.observe('visible', this.onVisibleChanged);
        super.on('onAddUnderlierBtnClick', this.onAddUnderlierBtnClicked);
        super.on('onCancelAllBtnClick', this.onCancelAllBtnClicked);
        super.on('onPlaceSelectedBtnClick', this.onPlaceSelectedBtnClicked);
    }
    onteardown() {
        if (!isNullOrUndefined(this._optionTrader)) {
            this._optionTrader.unsubscribeOnRepopulate(this.clearTable);
            this._optionTrader.unsubscribeOnCreatePaperPosition(this.onAddPaperPosition);
            this._optionTrader.unsubscribeOnRemovePaperPosition(this.onRemovePaperPosition);
            this._optionTrader.unsubscribeOnUpdatePaperPosition(this.onUpdatePaperPosition);
            DataCache.OnUpdateInstrument.UnSubscribe(this.onUpdateInstrument, this);
        }
        if (!isNullOrUndefined(this._quickTable)) {
            this._quickTable.AfterEditItem.UnSubscribe(this.onAfterEditItem, this);
            this._quickTable.OnPaintedPictureButtonClick.UnSubscribe(this.onTableRowButtonClicked, this);
        }
        super.onteardown();
    }
    jbInit() {
        this.initializeVariables();
        super.jbInit();
        this.populateTableContextMenu();
        const qt = this._quickTable;
        if (qt) {
            qt.beforeTableContextMenuShowing = this.preparePopup.bind(this);
            qt.InitializeDirect(new PaperPositionItem(null, null, null));
            this.SetColumnsDefaultDisplayIndex(qt);
            qt.UpdateSortedColumns();
            qt.AfterEditItem.Subscribe(this.onAfterEditItem, this);
            qt.OnPaintedPictureButtonClick.Subscribe(this.onTableRowButtonClicked, this);
            qt.columns[PaperPositionItem.ORDER_TYPE_COL_INDEX].renderer = new OrderTypeColumnRenderer();
        }
    }
    getQuickTable() {
        return this._quickTable;
    }
    getInstrument() {
        const table = this.getQuickTable();
        if (isNullOrUndefined(table)) {
            return null;
        }
        const selectedItem = table.getSelectedTableItems()[0];
        if (isNullOrUndefined(selectedItem)) {
            return null;
        }
        return selectedItem.GetCurrentInstrument();
    }
    layoutTable() {
        super.layoutTable();
        for (const table of super.findAllComponents('quickTableRactive')) {
            void super.layoutTableResize(table);
        }
    }
    localize() {
        super.localize();
        if (!this._isInitialized) {
            return;
        }
        this._quickTable.localize();
        void this.set({
            addUnderlierButtonText: Resources.getResource('panel.optionPaperPositions.addUnderlier'),
            cancelAllButtonText: Resources.getResource('panel.optionPaperPositions.cancelAll'),
            placeSelectedButtonText: Resources.getResource('panel.optionPaperPositions.placeSelected'),
            shortPositionsAreForbiddenText: Resources.getResource('panel.optionPaperPositions.shortPositionsAreForbidden')
        });
    }
    callThemeChange() {
        this.themeChange();
    }
    callCallback(properties) {
        this.callBack(properties);
    }
    themeChange() {
        super.themeChange();
        if (!this._isInitialized) {
            return;
        }
        const quickTableRactive = this.findComponent('quickTableRactive');
        quickTableRactive === null || quickTableRactive === void 0 ? void 0 : quickTableRactive.themeChange();
    }
    TickAsync() {
        super.TickAsync();
        const isVisible = this.get('visible');
        if (!isVisible) {
            return;
        }
        this.getQuickTable().needRedrawBackground = true;
    }
    updateData() {
        let shortPositionsAreForbidden = false;
        const quickTable = this.getQuickTable();
        for (const row of quickTable.rowsArray) {
            if (row.isGroupRow) {
                continue;
            }
            const item = row.item;
            if (item.savedOrder != null &&
                item.savedOrder.Active &&
                !item.savedOrder.isPosition() &&
                this.IsShortPositionForbidden(item)) {
                row.BackColor = ThemeManager.colors.OptionMasterTestOnPaperDenyShortPositionsColor;
                shortPositionsAreForbidden = true;
            }
            else {
                row.BackColor = Color.Empty;
            }
        }
        void this.set({ shortPositionsAreForbiddenVisibility: shortPositionsAreForbidden });
    }
    onUpdateInstrument(instrument) {
        if (this._optionTrader.underlier === instrument) {
            void this.updateButtonsEnability();
        }
    }
    IsShortPositionForbidden(item) {
        try {
            if (item.savedOrder.Operation === OperationType.Buy) {
                return false;
            }
            const instr = item.savedOrder.Instrument;
            if (instr.TradingPositionType === TradingPositionType.MultiPosition) {
                return true;
            }
            if (instr.RiskSettings.IsShortable(item.savedOrder.ProductType)) {
                return false;
            }
            let buyPaperPositionsAmount = 0;
            let sellPaperPositionsAmount = 0;
            const positions = this._optionTrader.getPaperPositionsForAnalyzer();
            for (const paperPos of positions) {
                if ((!paperPos.Active && !paperPos.isPosition()) ||
                    !Instrument.IsEqualInstrument(instr, paperPos.Instrument) ||
                    (paperPos.isPosition() && paperPos.isSubPosition())) {
                    continue;
                }
                if (paperPos.Operation === OperationType.Buy) {
                    buyPaperPositionsAmount += paperPos.QuantityLots;
                }
                if (paperPos.Operation === OperationType.Sell) {
                    sellPaperPositionsAmount += paperPos.QuantityLots;
                }
            }
            return sellPaperPositionsAmount > buyPaperPositionsAmount;
        }
        catch (ex) {
            return false;
        }
    }
    repopulate() {
        super.repopulate();
        this.clearTable();
    }
    updateSettings() {
        super.updateSettings();
        const rows = this.getQuickTable().rowsArray;
        for (let i = 0; i < rows.length; i++) {
            if (rows[i].isGroupRow) {
                continue;
            }
            const item = rows[i].item;
            item.updateEditingInfo();
            this.processEditRow(rows[i]);
        }
    }
    setOptionTrader(optionTrader) {
        this._optionTrader = optionTrader;
        this._optionTrader.subscribeOnRepopulate(this.clearTable);
        this._optionTrader.subscribeOnCreatePaperPosition(this.onAddPaperPosition);
        this._optionTrader.subscribeOnRemovePaperPosition(this.onRemovePaperPosition);
        this._optionTrader.subscribeOnUpdatePaperPosition(this.onUpdatePaperPosition);
        DataCache.OnUpdateInstrument.Subscribe(this.onUpdateInstrument, this);
    }
    /**
     * Sets the default display order of columns in the table based on the column parameters of PaperPositionItem.
     * Iterates over each column in the table and assigns the display index
     * using the saved menu index for each column's header text.
     *
     * @param {QuickTable} table - The table object containing the columns to update.
     * @param {Array} table.columns - An array of column objects in the table.
     * @param {Object} table.columns[].headerText - The header text of each column used to retrieve the saved menu index.
     * @param {number} table.columns[].displayedIndex - The index that determines the display order of the column.
     */
    SetColumnsDefaultDisplayIndex(table) {
        table.columns.forEach((column, index) => {
            const menuIndex = PaperPositionItem.GetMenuIndex(column.headerText);
            table.columns[index].displayedIndex = menuIndex;
        });
    }
    initializeVariables() {
        const quickTableRactive = super.findAllComponents('quickTableRactive')[0];
        this._quickTable = quickTableRactive.quickTable;
        this._isInitialized = true;
    }
    populateTableContextMenu() {
        const contextMenuItems = [
            {
                type: NewContextMenuItemType.Default,
                text: 'panel.positions.menu.View',
                tag: 'View',
                svgImage: 'View',
                visible: !Resources.isHidden('contextMenu.Totals.visibility'),
                subitems: [
                    {
                        type: NewContextMenuItemType.Default,
                        text: 'panel.optionPaperPositions.menu.ShowTotals',
                        tag: 'ShowTotals',
                        checked: false,
                        canCheck: true,
                        event: super.ShowTotalsStateChange.bind(this)
                    }
                ]
            }
        ];
        this.AddSymbolInfoContextMenuItemIfNeed(contextMenuItems);
        this._quickTable.setTableContextMenuItems(contextMenuItems);
        this.menuTagDict = createTagDictionary(contextMenuItems);
    }
    onVisibleChanged() {
        if (!this._isInitialized) {
            return;
        }
        this.layoutTable();
    }
    getTableRow(paperPosition) {
        for (let i = 0; i < this._quickTable.rowsArray.length; i++) {
            const row = this._quickTable.rowsArray[i];
            if (row.isGroupRow) {
                continue;
            }
            const item = row.item;
            if (item.savedOrder === paperPosition) {
                return row;
            }
        }
        return undefined;
    }
    onAddPaperPosition(paperPosition) {
        let row = this.getTableRow(paperPosition);
        if (!isNullOrUndefined(row)) {
            this.onUpdatePaperPosition(paperPosition);
            return;
        }
        row = this._quickTable.AddItem(new PaperPositionItem(this._optionTrader, paperPosition, SessionSettings));
        this.processEditRow(row);
        row.FillRowByItem(row.item, true);
        this._quickTable.redraw();
        void this.updateButtonsEnability();
    }
    onRemovePaperPosition(paperPosition) {
        const row = this.getTableRow(paperPosition);
        if (isNullOrUndefined(row)) {
            return;
        }
        this._quickTable.RemoveItem(row.item.ItemId);
        this._quickTable.redraw();
        void this.updateButtonsEnability();
    }
    onUpdatePaperPosition(paperPosition) {
        const row = this.getTableRow(paperPosition);
        if (isNullOrUndefined(row)) {
            return;
        }
        this.processEditRow(row);
        row.FillRowByItem(row.item, true);
        if (this._quickTable.groupByColumnIndex === PaperPositionItem.OPERATION_COL_INDEX ||
            this._quickTable.groupByColumnIndex === PaperPositionItem.QUANTITY_COL_INDEX) {
            this._quickTable.regroupAllWithSameCollapseState(); // #122848, #123026
        }
        this._quickTable.redraw();
        void this.updateButtonsEnability();
    }
    onAfterEditItem(data) {
        var _a, _b, _c;
        const tableItem = data.row.item;
        switch (data.realColumnIndex) {
            case PaperPositionItem.STRIKE_COL_INDEX:
                this._optionTrader.changeInstrumentForPaperPosition(tableItem.savedOrder.Instrument, data.newValue);
                break;
            case PaperPositionItem.QUANTITY_COL_INDEX:
                if (data.newValue === 0) {
                    this._optionTrader.removePaperPosition(tableItem.savedOrder.Instrument);
                }
                else {
                    const sign = Math.sign(data.newValue);
                    const lots = Quantity.convertAmountToLots(Math.abs(data.newValue), tableItem.savedOrder.Instrument);
                    tableItem.savedOrder.QuantityLots = lots;
                    tableItem.savedOrder.Operation = sign > 0 ? OperationType.Buy : OperationType.Sell;
                    if (tableItem.savedOrder.OrderType === OrderType.Limit) {
                        tableItem.savedOrder.Price = tableItem.savedOrder.Bid;
                    }
                }
                break;
            case PaperPositionItem.CASH_COL_INDEX:
                tableItem.savedOrder.Cash = data.newValue;
                break;
            case PaperPositionItem.ORDER_TYPE_COL_INDEX:
                tableItem.savedOrder.OrderType = data.newValue;
                tableItem.savedOrder.setDefaultPrices();
                tableItem.savedOrder.setLinkedPrice();
                break;
            case PaperPositionItem.PRICE_COL_INDEX:
                tableItem.savedOrder.Price = data.newValue;
                break;
            case PaperPositionItem.STOP_PRICE_COL_INDEX:
                if (tableItem.savedOrder.OrderType === OrderType.TrailingStop) {
                    const offsetType = SlTpUtils.getDefaultVisualOffsetTypeExcludePercent(tableItem.savedOrder.Instrument);
                    tableItem.savedOrder.StopPrice = SlTpUtils.toRealValue(data.newValue, tableItem.savedOrder.Instrument, offsetType);
                }
                else {
                    tableItem.savedOrder.StopPrice = data.newValue;
                }
                break;
            case PaperPositionItem.TIF_COL_INDEX:
                tableItem.savedOrder.TIF = data.newValue.type;
                tableItem.savedOrder.TIFExpiration = data.newValue.expirationTime;
                break;
            case PaperPositionItem.SL_PRICE_COL_INDEX:
                tableItem.savedOrder.SLTPHolder.StopLossLimitPriceValue = tableItem.savedOrder.SllPrice;
                tableItem.savedOrder.SLTPHolder.StopLossPriceType = SlTpPriceType.Absolute;
                tableItem.savedOrder.SLTPHolder.StopLossPriceValue = data.newValue === 0 ? NaN : data.newValue;
                break;
            case PaperPositionItem.SLL_PRICE_COL_INDEX:
                tableItem.savedOrder.SLTPHolder.StopLossPriceValue = tableItem.savedOrder.SlPrice;
                tableItem.savedOrder.SLTPHolder.StopLossPriceType = SlTpPriceType.Absolute;
                tableItem.savedOrder.SLTPHolder.StopLossLimitPriceValue = data.newValue === 0 ? NaN : data.newValue;
                break;
            case PaperPositionItem.TP_PRICE_COL_INDEX:
                tableItem.savedOrder.SLTPHolder.TakeProfitPriceType = SlTpPriceType.Absolute;
                tableItem.savedOrder.SLTPHolder.TakeProfitPriceValue = data.newValue === 0 ? NaN : data.newValue;
                break;
            case PaperPositionItem.SL_OFFSET_COL_INDEX:
                if (data.newValue !== 0) {
                    tableItem.savedOrder.SLTPHolder.StopLossLimitPriceValue = tableItem.savedOrder.SllOffset;
                    const slVisualOffsetType = (_a = SlTpUtils.getSLVisualOffsetType(tableItem.savedOrder.Instrument, tableItem.savedOrder.SLTPHolder)) !== null && _a !== void 0 ? _a : SlTpUtils.getDefaultVisualOffsetType(tableItem.savedOrder.Instrument);
                    tableItem.savedOrder.SLTPHolder.StopLossPriceType = SlTpUtils.getSLTPPriceType(slVisualOffsetType);
                    tableItem.savedOrder.SLTPHolder.StopLossPriceValue = SlTpUtils.toRealValue(data.newValue, tableItem.savedOrder.Instrument, slVisualOffsetType);
                    if (BaseSettings.isUseStopLimitInsteadStop() && tableItem.savedOrder.SLTPHolder.isSllEmpty()) {
                        tableItem.savedOrder.SLTPHolder.StopLossLimitPriceValue = BaseSettings.limitOffsetTicks;
                    }
                }
                else {
                    tableItem.savedOrder.SLTPHolder.StopLossPriceType = SlTpPriceType.Absolute;
                    tableItem.savedOrder.SLTPHolder.StopLossPriceValue = NaN;
                    tableItem.savedOrder.SLTPHolder.StopLossLimitPriceValue = NaN;
                }
                break;
            case PaperPositionItem.SLL_OFFSET_COL_INDEX:
                if (data.newValue !== 0) {
                    tableItem.savedOrder.SLTPHolder.StopLossLimitPriceValue = tableItem.savedOrder.SllOffset;
                    const slVisualOffsetType = (_b = SlTpUtils.getSLVisualOffsetType(tableItem.savedOrder.Instrument, tableItem.savedOrder.SLTPHolder)) !== null && _b !== void 0 ? _b : SlTpUtils.getDefaultVisualOffsetType(tableItem.savedOrder.Instrument);
                    tableItem.savedOrder.SLTPHolder.StopLossPriceType = SlTpUtils.getSLTPPriceType(slVisualOffsetType);
                    tableItem.savedOrder.SLTPHolder.StopLossLimitPriceValue = SlTpUtils.toRealValue(data.newValue, tableItem.savedOrder.Instrument, slVisualOffsetType);
                }
                else {
                    tableItem.savedOrder.SLTPHolder.StopLossPriceType = SlTpPriceType.Absolute;
                    tableItem.savedOrder.SLTPHolder.StopLossPriceValue = NaN;
                    tableItem.savedOrder.SLTPHolder.StopLossLimitPriceValue = NaN;
                }
                break;
            case PaperPositionItem.TP_OFFSET_COL_INDEX:
                if (data.newValue !== 0) {
                    const tpVisualOffsetType = (_c = SlTpUtils.getTPVisualOffsetType(tableItem.savedOrder.Instrument, tableItem.savedOrder.SLTPHolder)) !== null && _c !== void 0 ? _c : SlTpUtils.getDefaultVisualOffsetType(tableItem.savedOrder.Instrument);
                    tableItem.savedOrder.SLTPHolder.TakeProfitPriceType =
                        SlTpUtils.getSLTPPriceType(tpVisualOffsetType);
                    tableItem.savedOrder.SLTPHolder.TakeProfitPriceValue = SlTpUtils.toRealValue(data.newValue, tableItem.savedOrder.Instrument, tpVisualOffsetType);
                }
                else {
                    tableItem.savedOrder.SLTPHolder.TakeProfitPriceType = SlTpPriceType.Absolute;
                    tableItem.savedOrder.SLTPHolder.TakeProfitPriceValue = NaN;
                }
                break;
            case PaperPositionItem.TRAILING_STOP_COL_INDEX:
                tableItem.savedOrder.SLTPHolder.StopLossPriceValue = tableItem.savedOrder.SlOffset;
                tableItem.savedOrder.SLTPHolder.StopLossPriceType = tableItem.savedOrder.SLTPHolder.isTrailingStop()
                    ? SlTpPriceType.Offset
                    : SlTpPriceType.TrOffset;
                break;
            case PaperPositionItem.LINKED_PRICE_OFFSET_COL_INDEX:
                tableItem.savedOrder.LinkedPriceOffset = SlTpUtils.toRealValue(data.newValue, tableItem.savedOrder.Instrument, SlTpUtils.getDefaultVisualOffsetTypeExcludePercent(tableItem.savedOrder.Instrument));
                tableItem.savedOrder.setLinkedPrice();
                break;
            case PaperPositionItem.LINK_COL_INDEX:
                tableItem.savedOrder.LinkedPriceType = data.newValue;
                tableItem.savedOrder.setLinkedPrice();
                break;
            case PaperPositionItem.LEVERAGE_COL_INDEX:
                tableItem.savedOrder.Leverage = data.newValue;
                break;
            case PaperPositionItem.SEND_COL_INDEX:
                tableItem.savedOrder.Active = !tableItem.savedOrder.Active;
                break;
            case PaperPositionItem.ANALYSE_COL_INDEX:
                tableItem.savedOrder.Analyse = !tableItem.savedOrder.Analyse;
                break;
        }
        this._optionTrader.updatePaperPosition(tableItem.savedOrder.Instrument);
        this.processEditRow(data.row);
        data.row.FillRowByItem(tableItem, true);
        void this.updateButtonsEnability();
        this._quickTable.redraw();
    }
    processEditRow(row) {
        const item = row.item;
        const savedOrder = row.item.savedOrder;
        for (let i = 0; i < row.cells.length; i++) {
            if (savedOrder.isPosition() &&
                i !== PaperPositionItem.ANALYSE_COL_INDEX &&
                i !== PaperPositionItem.OPERATION_COL_INDEX) {
                row.cells[i].ReadOnly = true;
                continue;
            }
            switch (i) {
                case PaperPositionItem.PRODUCT_TYPE_COL_INDEX:
                    row.cells[i].ReadOnly = !item.isExistProductTypes;
                    break;
                case PaperPositionItem.CASH_COL_INDEX:
                    row.cells[i].ReadOnly = isNullOrUndefined(savedOrder.Cash) || savedOrder.Cash <= 0;
                    break;
                case PaperPositionItem.QUANTITY_COL_INDEX:
                    row.cells[i].ReadOnly = savedOrder.Cash > 0;
                    break;
                case PaperPositionItem.STRIKE_COL_INDEX:
                    row.cells[i].ReadOnly = !savedOrder.Instrument.isOptionSymbol;
                    break;
                case PaperPositionItem.PRICE_COL_INDEX:
                    if (savedOrder.LinkedPriceType !== LinkedPriceType.None) {
                        row.cells[i].ReadOnly = true;
                    }
                    else {
                        row.cells[i].ReadOnly =
                            savedOrder.OrderType !== OrderType.Limit &&
                                savedOrder.OrderType !== OrderType.StopLimit &&
                                savedOrder.OrderType !== OrderType.OCO;
                    }
                    break;
                case PaperPositionItem.STOP_PRICE_COL_INDEX:
                    if (savedOrder.LinkedPriceType !== LinkedPriceType.None) {
                        row.cells[i].ReadOnly = true;
                    }
                    else {
                        row.cells[i].ReadOnly =
                            savedOrder.OrderType !== OrderType.Stop &&
                                savedOrder.OrderType !== OrderType.StopLimit &&
                                savedOrder.OrderType !== OrderType.TrailingStop &&
                                savedOrder.OrderType !== OrderType.OCO;
                    }
                    break;
                case PaperPositionItem.LINK_COL_INDEX:
                    row.cells[i].ReadOnly =
                        savedOrder.OrderType !== OrderType.Limit && savedOrder.OrderType !== OrderType.Stop;
                    break;
                case PaperPositionItem.LINKED_PRICE_OFFSET_COL_INDEX:
                    row.cells[i].ReadOnly =
                        savedOrder.LinkedPriceType === LinkedPriceType.None ||
                            (savedOrder.OrderType !== OrderType.Limit && savedOrder.OrderType !== OrderType.Stop);
                    break;
                case PaperPositionItem.LEVERAGE_COL_INDEX:
                    row.cells[i].ReadOnly =
                        !isNullOrUndefined(savedOrder.Instrument) &&
                            !savedOrder.Instrument.isLeverageVisible(savedOrder.Account, savedOrder.ProductType);
                    break;
                case PaperPositionItem.SL_PRICE_COL_INDEX:
                    row.cells[i].ReadOnly =
                        (isNaN(savedOrder.SlPrice) && !SavedOrderValidator.isAllowSl(savedOrder)) ||
                            savedOrder.SLTPHolder.StopLossPriceType === SlTpPriceType.TrOffset;
                    break;
                case PaperPositionItem.SL_OFFSET_COL_INDEX:
                    row.cells[i].ReadOnly = isNaN(savedOrder.SlPrice) && !SavedOrderValidator.isAllowSl(savedOrder);
                    break;
                case PaperPositionItem.TRAILING_STOP_COL_INDEX:
                    row.cells[i].ReadOnly = isNaN(savedOrder.SlOffset);
                    break;
                case PaperPositionItem.SLL_PRICE_COL_INDEX:
                case PaperPositionItem.SLL_OFFSET_COL_INDEX:
                    row.cells[i].ReadOnly =
                        isNaN(savedOrder.SllPrice) &&
                            (!SavedOrderValidator.isAllowSl(savedOrder) || !WDSettings.isUseStopLimitInsteadStop());
                    break;
                case PaperPositionItem.TP_PRICE_COL_INDEX:
                case PaperPositionItem.TP_OFFSET_COL_INDEX:
                    row.cells[i].ReadOnly = isNaN(savedOrder.TpPrice) && !SavedOrderValidator.isAllowTp(savedOrder);
                    break;
            }
        }
    }
    onTableRowButtonClicked(args) {
        const tableItem = args.row.item;
        switch (args.realColumnIndex) {
            case PaperPositionItem.REMOVE_COL_INDEX:
                this._optionTrader.removePaperPosition(tableItem.savedOrder.Instrument);
                break;
        }
    }
    updateButtonsEnability() {
        return __awaiter(this, void 0, void 0, function* () {
            const rows = this._quickTable.rowsArray;
            let haveActiveRows = false;
            let isTradingAllowed = true;
            let isCancelAllEnabled = false;
            for (let i = 0; i < rows.length; i++) {
                if (rows[i].isGroupRow) {
                    continue;
                }
                const item = rows[i].item;
                if (!isCancelAllEnabled && !item.savedOrder.isPosition()) {
                    isCancelAllEnabled = true;
                }
                if (!item.savedOrder.Active) {
                    continue;
                }
                haveActiveRows = true;
                isTradingAllowed = IsAllowed.IsTradingAllowed(item.savedOrder.Account, item.savedOrder.Instrument, item.savedOrder.OrderType).Allowed;
                if (!isTradingAllowed) {
                    break;
                }
            }
            yield this.set('isPlaceSelectedEnabled', haveActiveRows && isTradingAllowed);
            yield this.set('isCancelAllEnabled', isCancelAllEnabled);
        });
    }
    onAddUnderlierBtnClicked() {
        var _a;
        (_a = MainWindowManager.Factory) === null || _a === void 0 ? void 0 : _a.addPanel(PanelNames.AdvancedOrderEntry, null, this.onOpenOrderEntryPanel);
    }
    onCancelAllBtnClicked() {
        this._optionTrader.clearPaperPositions();
    }
    onPlaceSelectedBtnClicked() {
        return __awaiter(this, void 0, void 0, function* () {
            const rows = this._quickTable.rowsArray;
            const ordersForPlace = [];
            for (let i = 0; i < rows.length; i++) {
                if (rows[i].isGroupRow) {
                    continue;
                }
                const item = rows[i].item;
                if (item.savedOrder.Active) {
                    ordersForPlace.push(item.savedOrder);
                }
            }
            yield SavedOrdersController.placeOrders(ordersForPlace, PlacedFrom.WEB_OPTION_MASTER, BaseSettings.confirmOrderSending);
        });
    }
}
ApplicationPanelWithTable.extendWith(OptionPaperPositionsPanel, {
    template: OptionPaperPositionsPanelTemplate,
    data: function () {
        return {
            addUnderlierButtonText: '',
            cancelAllButtonText: '',
            placeSelectedButtonText: '',
            shortPositionsAreForbiddenText: '',
            isPlaceSelectedEnabled: false,
            isCancelAllEnabled: false,
            shortPositionsAreForbiddenVisibility: false
        };
    }
});
